import { supabase } from "../supabase/client";

export type MortgageCalculatorSettings = {
  version: 1;
  homePrice: number;
  creditScore: number;
  downPaymentPct: number;
  termYears: number;
  propertyTaxRatePct: number;
  annualHomeownersInsurance: number;
  interestRateMode: "modeled" | "override";
  interestRateInputPct: string;
  homeType: string;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  taxProfileId: string;
  manualFederalMarginalRatePct: number;
  manualStateMarginalRatePct: number;
  saltCap: number;
  otherSaltUsed: number;
  mortgageInterestDebtLimit: number;
};

export type MortgageCalculatorProfile = {
  id: string;
  name: string;
  settings: MortgageCalculatorSettings;
  createdAt: number;
};

type DbMortgageCalculatorProfile = {
  id: string;
  name: string;
  settings: any;
  created_at: string;
};

export const MORTGAGE_CALCULATOR_PROFILES_CHANGED_EVENT =
  "mortgageCalculatorProfiles:changed";

export const notifyMortgageCalculatorProfilesChanged = () => {
  window.dispatchEvent(new Event(MORTGAGE_CALCULATOR_PROFILES_CHANGED_EVENT));
};

export const onMortgageCalculatorProfilesChanged = (handler: () => void) => {
  window.addEventListener(MORTGAGE_CALCULATOR_PROFILES_CHANGED_EVENT, handler);
  return () =>
    window.removeEventListener(
      MORTGAGE_CALCULATOR_PROFILES_CHANGED_EVENT,
      handler
    );
};

const mapDbToProfile = (
  row: DbMortgageCalculatorProfile
): MortgageCalculatorProfile => {
  return {
    id: row.id,
    name: row.name,
    settings: row.settings,
    createdAt: Number.isFinite(Date.parse(row.created_at))
      ? Date.parse(row.created_at)
      : Date.now(),
  };
};

export const mortgageCalculatorProfilesDB = {
  getAll: async (): Promise<MortgageCalculatorProfile[]> => {
    const { data, error } = await supabase
      .from("mortgage_calculator_profiles")
      .select("id,name,settings,created_at")
      .order("created_at", { ascending: true });
    if (error) {
      console.error("[mortgageCalculatorProfilesDB] getAll failed", error);
      return [];
    }
    return (data || []).map(mapDbToProfile);
  },

  upsert: async (
    profile: MortgageCalculatorProfile
  ): Promise<MortgageCalculatorProfile | null> => {
    const { data, error } = await supabase
      .from("mortgage_calculator_profiles")
      .upsert(
        [
          {
            id: profile.id,
            name: profile.name,
            settings: profile.settings,
          },
        ],
        { onConflict: "id" }
      )
      .select("id,name,settings,created_at")
      .single();
    if (error) {
      console.error("[mortgageCalculatorProfilesDB] upsert failed", error);
      return null;
    }
    notifyMortgageCalculatorProfilesChanged();
    return mapDbToProfile(data as DbMortgageCalculatorProfile);
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from("mortgage_calculator_profiles")
      .delete()
      .eq("id", id);
    if (error) {
      console.error("[mortgageCalculatorProfilesDB] delete failed", error);
      return false;
    }
    notifyMortgageCalculatorProfilesChanged();
    return true;
  },
};
